import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, X, AlertTriangle, Camera, MapPin, Loader2, TrendingUp, TrendingDown, Download, RefreshCw, CheckCheck, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ZoomableImage } from '../../components/ZoomableImage';
import { FUEL_TYPES } from '../../mock/mock';
import { api, fileURL, photoURL, API_BASE } from '../../lib/api';
import { toast } from 'sonner';

const AdminPrices = ({ embedded = false }) => {
  const [entries, setEntries] = useState([]);
  const [diffMap, setDiffMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [tab, setTab] = useState('all');
  const [showHistory, setShowHistory] = useState(false); // false → últimos 2 días · true → últimos 30 días
  const [photoOpen, setPhotoOpen] = useState(null);
  const [approvedPrices, setApprovedPrices] = useState({});
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState({ pending: 0, entries: 0, pushes: 0 });
  const [zipBusy, setZipBusy] = useState(false);
  const [zipProgress, setZipProgress] = useState(null); // {lot, files, remaining}
  const zipCancelRef = React.useRef(false);
  const [stationFilter, setStationFilter] = useState('all');
  const [capturerFilter, setCapturerFilter] = useState('all');
  const [q, setQ] = useState('');
  const [buById, setBuById] = useState({});
  const [summary, setSummary] = useState(null);
  const [searchParams] = useSearchParams();
  const deepLinkDone = useRef(false);

  // Deep-link desde notificaciones: /admin/prices?tab=flagged&entry={id}
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const mapped = tabParam === 'flagged' || tabParam === 'pending' ? 'anomaly' : tabParam;
    if (mapped && ['all', 'anomaly'].includes(mapped)) setTab(mapped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (deepLinkDone.current || loading) return;
    const eid = searchParams.get('entry');
    if (!eid) { deepLinkDone.current = true; return; }
    deepLinkDone.current = true;
    const e = entries.find(x => x.id === eid);
    if (e) {
      const t = e.captured_at ? Date.parse(e.captured_at) : 0;
      if (t < Date.now() - 2 * 86400 * 1000) setShowHistory(true);
      setOpenGroups(prev => new Set(prev).add(e.own_station_name || 'Sin estación propia asignada'));
      openDetail(e);
    } else {
      toast.error('No se encontró la captura de la notificación (puede haber sido depurada)');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, entries]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/prices', { params: { limit: 500 } });
      setEntries(r.data);
      api.get('/stations').then(sr => {
        const m = {};
        (sr.data || []).forEach(s => { if (s.bu) m[s.id] = s.bu; });
        setBuById(m);
      }).catch(() => {});
      const [pen, flag] = await Promise.all([
        api.get('/prices/competitor-diff', { params: { status_f: 'pending' } }).catch(() => ({ data: [] })),
        api.get('/prices/competitor-diff', { params: { status_f: 'flagged' } }).catch(() => ({ data: [] })),
      ]);
      const m = {};
      [...pen.data, ...flag.data].forEach(e => { m[e.id] = e; });
      setDiffMap(m);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const refreshPending = () => api.get('/photos/export/pending-count').then(r => setPending(r.data)).catch(() => {});
  useEffect(() => { refreshPending(); }, []);

  // Rango de fechas (auditoría): cuenta pendientes dentro del rango seleccionado
  const [range, setRange] = useState({ from: '', to: '' });
  const [rangePending, setRangePending] = useState(null);
  useEffect(() => {
    if (!range.from || !range.to) { setRangePending(null); return; }
    api.get('/photos/export/pending-count', { params: { date_from: range.from, date_to: range.to } })
      .then(r => setRangePending(r.data)).catch(() => setRangePending(null));
  }, [range.from, range.to]);

  useEffect(() => {
    api.get('/prices/anomaly-summary', { params: { days: 7 } })
      .then(r => setSummary(r.data)).catch(() => {});
  }, []);

  // Descarga CONTINUA: baja ZIPs lote tras lote hasta vaciar las fotos pendientes.
  // El navegador pedirá permiso para "descargar varios archivos" la primera vez.
  const downloadZip = async (onlyNew = true, useRange = false) => {
    setZipBusy(true);
    zipCancelRef.current = false;
    let lot = 0;
    let files = 0;
    const rangeQS = useRange && range.from && range.to ? `&date_from=${range.from}&date_to=${range.to}` : '';
    try {
      const token = localStorage.getItem('pc_token');
      for (;;) {
        const resp = await fetch(`${API_BASE}/photos/export/zip?only_new=${onlyNew ? 'true' : 'false'}&batch=200${rangeQS}`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (resp.status === 204) {
          toast(lot === 0 ? 'Sin fotos nuevas para descargar' : `Listo · ${files} foto(s) en ${lot} ZIP(s)`);
          break;
        }
        if (!resp.ok) { toast.error(`Error al generar ZIP (lote ${lot + 1})`); break; }
        const count = parseInt(resp.headers.get('X-Exported-Count') || '0', 10);
        const remaining = parseInt(resp.headers.get('X-Remaining') || '0', 10);
        const blob = await resp.blob();
        const url = window.URL.createObjectURL(blob);
        const cd = resp.headers.get('Content-Disposition') || '';
        const m = /filename="?([^";]+)"?/.exec(cd);
        const fname = (m && m[1]) || `pricecapture-photos-lote${lot + 1}.zip`;
        const a = document.createElement('a');
        a.href = url; a.download = fname; document.body.appendChild(a); a.click();
        a.remove(); window.URL.revokeObjectURL(url);
        lot += 1;
        files += count;
        setZipProgress({ lot, files, remaining });
        refreshPending();
        if (!onlyNew) { toast.success(`${count} foto(s) descargadas`); break; }
        if (remaining <= 0) {
          toast.success(`Historial al día · ${files} foto(s) en ${lot} ZIP(s)`);
          break;
        }
        if (zipCancelRef.current) {
          toast(`Descarga detenida · ${files} foto(s) en ${lot} ZIP(s) · quedan ${remaining}`);
          break;
        }
        await new Promise(r => setTimeout(r, 800));
      }
    } finally {
      setZipBusy(false);
      setZipProgress(null);
      refreshPending();
      if (useRange && range.from && range.to) {
        api.get('/photos/export/pending-count', { params: { date_from: range.from, date_to: range.to } })
          .then(r => setRangePending(r.data)).catch(() => {});
      }
    }
  };

  const resetDownloaded = async () => {
    if (!window.confirm('¿Marcar todas las fotos como NO descargadas? La próxima descarga incluirá todo el histórico de 30 días.')) return;
    try {
      const r = await api.post('/photos/export/reset-downloaded');
      toast.success(`${r.data.reset} registro(s) reiniciado(s)`);
      refreshPending();
    } catch { toast.error('Error al reiniciar'); }
  };

  const purgeDownloaded = async () => {
    if (!window.confirm('¿Vaciar el historial de fotos YA descargadas?\n\nSe conservan SIEMPRE los últimos 30 días de fotos en la app (auditoría) — solo se retiran las descargadas más viejas de un mes. Los precios y registros NO se tocan — el ZIP descargado queda como archivo.')) return;
    try {
      const r = await api.post('/photos/export/purge-downloaded');
      toast.success(`Historial vaciado · ${(r.data.purged_entries || 0) + (r.data.purged_pushes || 0)} foto(s) retiradas de la app`);
      refreshPending();
    } catch { toast.error('Error al vaciar historial'); }
  };

  const cutoffDays = showHistory ? 30 : 2;
  const cutoffMs = Date.now() - cutoffDays * 86400 * 1000;
  const filtered = entries
    .filter(e => tab === 'all' || (tab === 'anomaly' ? (e.gps_anomaly && !e.anomaly_reviewed) : e.status === tab))
    .filter(e => stationFilter === 'all' || e.station_id === stationFilter)
    .filter(e => capturerFilter === 'all' || e.captured_by_id === capturerFilter)
    .filter(e => {
      if (!q.trim()) return true;
      const t = q.trim().toLowerCase();
      return [e.station_name, buById[e.station_id], e.captured_by_name, e.own_station_name]
        .some(x => (x || '').toLowerCase().includes(t));
    })
    .filter(e => {
      const t = e.captured_at ? Date.parse(e.captured_at) : 0;
      return t >= cutoffMs;
    });

  const stationOptions = React.useMemo(() => {
    const m = new Map();
    entries.forEach(e => { if (e.station_id && !m.has(e.station_id)) m.set(e.station_id, e.station_name); });
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [entries]);
  const capturerOptions = React.useMemo(() => {
    const m = new Map();
    entries.forEach(e => { if (e.captured_by_id && !m.has(e.captured_by_id)) m.set(e.captured_by_id, e.captured_by_name); });
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [entries]);
  const filtersActive = stationFilter !== 'all' || capturerFilter !== 'all';

  // Group rows by the OWN station whose tracking generated them
  const groups = React.useMemo(() => {
    const m = new Map();
    filtered.forEach(e => {
      const key = e.own_station_name || 'Sin estación propia asignada';
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(e);
    });
    return Array.from(m, ([name, rows]) => ({ name, rows }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [filtered]);
  const [openGroups, setOpenGroups] = useState(new Set());
  const toggleGroup = (name) => setOpenGroups(prev => {
    const n = new Set(prev);
    if (n.has(name)) n.delete(name); else n.add(name);
    return n;
  });
  const groupSig = groups.map(g => g.name).join('|');
  const autoExpandedSig = React.useRef('');
  useEffect(() => {
    // Auto-expand single group only once per group-set (doesn't fight manual collapse)
    if (groups.length === 1 && autoExpandedSig.current !== groupSig) {
      autoExpandedSig.current = groupSig;
      setOpenGroups(new Set([groups[0].name]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupSig]);

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const approveSelected = async () => {
    const ids = Array.from(selected);
    try {
      await api.post('/prices/bulk-approve', { ids });
      toast.success(`${ids.length} captura(s) aprobada(s)`);
      setSelected(new Set()); load();
    } catch { toast.error('Error al aprobar'); }
  };
  const rejectSelected = async () => {
    const ids = Array.from(selected);
    try {
      await api.post('/prices/bulk-reject', { ids });
      toast(`${ids.length} captura(s) rechazada(s)`);
      setSelected(new Set()); load();
    } catch { toast.error('Error al rechazar'); }
  };

  const markAnomalyReviewed = async (p) => {
    if (!window.confirm(`¿Marcar como REVISADA la anomalía de ${p.station_name}?\n\nSe archiva de la pestaña Anomalías sin aprobar ni rechazar la captura.`)) return;
    try {
      await api.post(`/prices/${p.id}/anomaly-reviewed`);
      toast.success('Anomalía marcada como revisada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openDetail = (entry) => {
    setPhotoOpen(entry);
    const initial = {};
    FUEL_TYPES.forEach(ft => {
      const ap = entry.approved_prices?.[ft.id];
      const cap = entry.prices?.[ft.id];
      initial[ft.id] = ap != null ? String(ap) : (cap != null ? String(cap) : '');
    });
    setApprovedPrices(initial);
    setReviewNotes(entry.review_notes || '');
  };
  const closeDetail = () => { setPhotoOpen(null); setApprovedPrices({}); setReviewNotes(''); };

  const submitApproval = async (action) => {
    if (!photoOpen) return;
    setSubmitting(true);
    try {
      if (action === 'reject') {
        await api.post(`/prices/${photoOpen.id}/reject`);
        toast.success('Captura rechazada');
      } else {
        const ap = {};
        Object.entries(approvedPrices).forEach(([k, v]) => {
          if (v !== '' && !isNaN(parseFloat(v))) ap[k] = parseFloat(v);
        });
        await api.post(`/prices/${photoOpen.id}/approve`, { approved_prices: ap, notes: reviewNotes || null });
        toast.success('Captura aprobada con precios definidos');
      }
      closeDetail();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  const anomalyCount = entries.filter(e => e.gps_anomaly && !e.anomaly_reviewed && (e.captured_at ? Date.parse(e.captured_at) : 0) >= cutoffMs).length;
  const hiddenByCutoff = entries.filter(e => (tab === 'all' || (tab === 'anomaly' ? (e.gps_anomaly && !e.anomaly_reviewed) : e.status === tab)) && (e.captured_at ? Date.parse(e.captured_at) : 0) < cutoffMs).length;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        {embedded ? <div/> : (
          <div>
            <h2 className="font-serif text-[36px] leading-tight tracking-tight">Aprobaciones</h2>
            <p className="text-[13px] text-[#5a5a5a] mt-1">Revisa, ajusta y aprueba los precios capturados en campo. El diferencial vs competidores se muestra en cada celda.</p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {selected.size > 0 && (
            <div className="flex gap-2 fade-in">
              <Button onClick={rejectSelected} variant="outline" className="h-9 rounded-full border-[#1c1c1c]/20 text-[12px]"><X size={13} className="mr-1"/> Rechazar ({selected.size})</Button>
              <Button onClick={approveSelected} className="h-9 rounded-full bg-[#1c1c1c] hover:bg-black text-[#f8f6f1] text-[12px]"><Check size={13} className="mr-1"/> Aprobar ({selected.size})</Button>
            </div>
          )}
          <Button
            onClick={() => downloadZip(true)}
            disabled={zipBusy || pending.pending === 0}
            variant="outline"
            className="h-9 rounded-full border-[#1c1c1c]/20 text-[12px]"
            data-testid="export-zip-btn"
            title="Descarga automáticamente ZIPs de 200 fotos lote tras lote hasta vaciar las pendientes + un CSV con estación, precios, GPS, capturador y fecha."
          >
            {zipBusy ? <Loader2 size={13} className="mr-1 animate-spin"/> : <Download size={13} className="mr-1"/>}
            {zipBusy && zipProgress
              ? `Lote ${zipProgress.lot} · ${zipProgress.files} fotos · quedan ${zipProgress.remaining}`
              : <>Descargar todo {pending.pending > 0 ? `(${pending.pending} nuevas)` : ''}</>}
          </Button>
          {zipBusy && (
            <Button
              onClick={() => { zipCancelRef.current = true; }}
              variant="ghost"
              className="h-9 rounded-full text-[11.5px] text-[#991b1b]"
              data-testid="zip-cancel-btn"
            >
              <X size={12} className="mr-1"/> Detener
            </Button>
          )}
          <div className="flex items-center gap-1.5 border border-[#e8e5de] rounded-full pl-3 pr-1 py-1 bg-white" title="Auditoría: descarga por rango de fechas (hora GT) las fotos aún no descargadas. Cada ZIP incluye resumen.xlsx y las fotos llevan correlativo + BU + estación + fecha-hora.">
            <span className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Rango</span>
            <input
              type="date"
              value={range.from}
              max={range.to || undefined}
              onChange={e => setRange(r => ({ ...r, from: e.target.value }))}
              className="h-7 text-[11.5px] bg-transparent outline-none text-[#1c1c1c]"
              data-testid="export-range-from"
            />
            <span className="text-[11px] text-[#8a8579]">→</span>
            <input
              type="date"
              value={range.to}
              min={range.from || undefined}
              onChange={e => setRange(r => ({ ...r, to: e.target.value }))}
              className="h-7 text-[11.5px] bg-transparent outline-none text-[#1c1c1c]"
              data-testid="export-range-to"
            />
            <Button
              onClick={() => downloadZip(true, true)}
              disabled={zipBusy || !range.from || !range.to || (rangePending && rangePending.pending === 0)}
              className="h-7 rounded-full bg-[#0f1d6f] hover:bg-[#0a1450] text-white text-[11px] px-3"
              data-testid="export-range-btn"
            >
              <Download size={11} className="mr-1"/>
              Descargar rango{rangePending != null ? ` (${rangePending.pending})` : ''}
            </Button>
          </div>
          <Button
            onClick={resetDownloaded}
            variant="ghost"
            className="h-9 rounded-full text-[11.5px] text-[#8a8579]"
            data-testid="reset-downloaded-btn"
            title="Marca todas como NO descargadas para volver a exportar todo el histórico de 30 días."
          >
            <RefreshCw size={12} className="mr-1"/> Reiniciar
          </Button>
          <Button
            onClick={purgeDownloaded}
            variant="ghost"
            className="h-9 rounded-full text-[11.5px] text-[#991b1b] hover:bg-[#fee2e2]"
            data-testid="purge-downloaded-btn"
            title="Vacía el historial: retira de la app las fotos que ya fueron descargadas en ZIP. Los precios y registros no se tocan."
          >
            <Trash2 size={12} className="mr-1"/> Vaciar descargadas
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-b border-[#e8e5de]">
        <div className="flex gap-2">
          {[['all','Todas'],['anomaly', `Anomalías · ${anomalyCount}`]].map(([k,l]) => (
            <button key={k} onClick={() => { setTab(k); setSelected(new Set()); }} className={`px-4 py-3 text-[12.5px] border-b-2 -mb-px ${tab===k?'border-[#1c1c1c] text-[#1c1c1c]':'border-transparent text-[#8a8579]'}`}>{l}</button>
          ))}
        </div>
        <button
          onClick={() => setShowHistory(v => !v)}
          className={`text-[11.5px] px-3 h-8 rounded-full border shrink-0 mb-2 ${showHistory ? 'bg-[#1c1c1c] text-[#f8f6f1] border-[#1c1c1c]' : 'bg-white text-[#5a5a5a] border-[#d4cfc4]'}`}
          data-testid="prices-history-toggle"
          title="Cambia entre vista rápida de 2 días y histórico de 30 días"
        >
          {showHistory ? 'Histórico (30 días)' : 'Últimos 2 días'}
        </button>
      </div>

      {/* Filtros por estación / capturador */}
      <div className="flex flex-wrap items-center gap-2" data-testid="prices-filters">
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar estación, BU, capturador…"
          className="h-9 w-[220px] bg-white text-[12px]" data-testid="prices-search"/>
        <select value={stationFilter} onChange={e => setStationFilter(e.target.value)}
          className="h-9 text-[12px] border border-[#e8e5de] rounded-lg px-2.5 bg-white min-w-[180px] max-w-[260px]"
          data-testid="prices-filter-station">
          <option value="all">Todas las estaciones</option>
          {stationOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <select value={capturerFilter} onChange={e => setCapturerFilter(e.target.value)}
          className="h-9 text-[12px] border border-[#e8e5de] rounded-lg px-2.5 bg-white min-w-[170px] max-w-[240px]"
          data-testid="prices-filter-capturer">
          <option value="all">Todos los capturadores</option>
          {capturerOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        {(filtersActive || q) && (
          <button onClick={() => { setStationFilter('all'); setCapturerFilter('all'); setQ(''); }}
            className="text-[11.5px] text-[#1c1c1c] underline" data-testid="prices-filters-clear">
            Limpiar filtros
          </button>
        )}
        <span className="text-[11px] text-[#8a8579] ml-auto" data-testid="prices-filtered-count">
          {filtered.length} captura(s)
        </span>
      </div>

      {/* Resumen semanal de anomalías GPS (visible en pestaña Marcadas) */}
      {tab === 'anomaly' && summary && summary.anomalies > 0 && (
        <div className="bg-white border border-[#e8e5de] rounded-xl p-5" data-testid="anomaly-summary-panel">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <AlertTriangle size={14} className="text-[#b45309]"/>
            <p className="text-[13px] font-medium">Resumen de anomalías GPS · últimos {summary.days} días</p>
            <span className="text-[11px] text-[#8a8579]">
              {summary.anomalies} de {summary.total_captures} capturas ({summary.pct}%) tomadas fuera del radio GPS permitido
            </span>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579] mb-1.5">Estaciones con más anomalías</p>
              {summary.by_station.map(s => (
                <button key={s.id} onClick={() => setStationFilter(s.id)}
                  className="w-full flex items-center gap-2 py-1.5 border-b border-[#f3f0e9] last:border-0 text-left hover:bg-[#f8f6f1] px-1.5 rounded transition"
                  title="Filtrar por esta estación" data-testid={`anomaly-station-row-${s.id}`}>
                  <span className="flex-1 text-[12.5px] truncate">{s.name}</span>
                  <span className="w-24 h-1.5 rounded-full bg-[#f3f0e9] overflow-hidden shrink-0">
                    <span className="bar-grow block h-full bg-[#b45309]" style={{ width: `${s.pct}%` }}/>
                  </span>
                  <span className="text-[11.5px] font-mono text-[#92400e] w-24 text-right shrink-0 whitespace-nowrap">{s.anomalies}/{s.total} · {s.pct}%</span>
                </button>
              ))}
            </div>
            <div>
              <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579] mb-1.5">Capturadores con más anomalías</p>
              {summary.by_capturer.map(c => (
                <button key={c.id} onClick={() => setCapturerFilter(c.id)}
                  className="w-full flex items-center gap-2 py-1.5 border-b border-[#f3f0e9] last:border-0 text-left hover:bg-[#f8f6f1] px-1.5 rounded transition"
                  title="Filtrar por este capturador" data-testid={`anomaly-capturer-row-${c.id}`}>
                  <span className="flex-1 text-[12.5px] truncate">{c.name}</span>
                  <span className="w-24 h-1.5 rounded-full bg-[#f3f0e9] overflow-hidden shrink-0">
                    <span className="bar-grow block h-full bg-[#b45309]" style={{ width: `${c.pct}%` }}/>
                  </span>
                  <span className="text-[11.5px] font-mono text-[#92400e] w-24 text-right shrink-0 whitespace-nowrap">{c.anomalies}/{c.total} · {c.pct}%</span>
                </button>
              ))}
            </div>
          </div>
          <p className="text-[10.5px] text-[#8a8579] mt-2">Click en una fila para filtrar la tabla. Incluye capturas ya revisadas.</p>
        </div>
      )}


      <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
        {loading ? <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div> : (
          <table className="w-full text-[13px]">
            <thead className="bg-[#f8f6f1]">
              <tr className="text-left text-[11px] tracking-wider uppercase text-[#8a8579] border-b border-[#e8e5de]">
                <th className="py-3 px-4 w-10"></th>
                <th className="py-3 px-2 font-normal">Estación</th>
                <th className="py-3 px-2 font-normal">Capturador</th>
                <th className="py-3 px-2 font-normal">Fecha</th>
                {FUEL_TYPES.map(ft => <th key={ft.id} className="py-3 px-2 font-normal text-right">{ft.name}</th>)}
                <th className="py-3 px-2 font-normal">Estado</th>
                <th className="py-3 px-4 font-normal text-right">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e8e5de]">
              {groups.map(g => {
                const open = openGroups.has(g.name);
                const pendingInGroup = g.rows.filter(r => r.gps_anomaly && !r.anomaly_reviewed).length;
                return (
                  <React.Fragment key={g.name}>
                    <tr className="bg-[#f8f6f1]/70">
                      <td colSpan={9} className="p-0">
                        <button
                          onClick={() => toggleGroup(g.name)}
                          className="w-full flex items-center gap-2.5 px-4 py-3 text-left hover:bg-[#f3f0e9] transition-colors"
                          data-testid={`group-toggle-${g.name.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          <span className={`text-[#8a8579] transition-transform duration-200 ${open ? 'rotate-90' : ''}`}>▸</span>
                          <span className="text-[13px] font-medium">{g.name}</span>
                          <span className="text-[11px] text-[#8a8579]">{g.rows.length} captura(s)</span>
                          {pendingInGroup > 0 && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#fee2e2] text-[#991b1b]">{pendingInGroup} anomalía(s)</span>
                          )}
                        </button>
                      </td>
                    </tr>
                    {open && g.rows.map(p => {
                const d = diffMap[p.id];
                return (
                <tr key={p.id} className="hover:bg-[#1c1c1c]/[0.04] transition-colors duration-150" data-testid={`price-row-${p.id}`}>
                  <td className="py-3 px-4">{(p.status === 'pending' || p.status === 'flagged') && <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)}/>}</td>
                  <td className="py-3 px-2">
                    <p className="font-medium">{p.station_name}</p>
                    <p className="text-[11px] text-[#8a8579] flex items-center gap-1">
                      <MapPin size={10}/> {p.brand} · {p.distance_m != null ? `disp ${p.distance_m}m` : 'sin GPS'}
                      <span className={`px-1.5 py-0.5 rounded-full text-[9.5px] font-medium ${p.service_type === 'full' ? 'bg-[#fef3c7] text-[#92400e]' : 'bg-[#e7eeff] text-[#0f1d6f]'}`} data-testid={`svc-tag-${p.id}`}>
                        {p.service_type === 'full' ? 'Completo' : 'Auto'}
                      </span>
                      {p.photo_gps_distance_m != null && (
                        <span
                          className={`ml-1 ${Math.abs(p.distance_m - p.photo_gps_distance_m) > 100 ? 'text-[#991b1b] font-medium' : ''}`}
                          title="Distancia según GPS del EXIF de la foto"
                        >
                          · foto {p.photo_gps_distance_m}m
                        </span>
                      )}
                      {d?.region ? ` · ${d.region}` : ''}
                    </p>
                  </td>
                  <td className="py-3 px-2 text-[#3a3a3a]">{p.captured_by_name}</td>
                  <td className="py-3 px-2 text-[12px] text-[#5a5a5a]">{new Date(p.captured_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}</td>
                  {FUEL_TYPES.map(ft => {
                    const v = p.prices?.[ft.id];
                    const cd = d?.competitor_diffs?.[ft.id];
                    return (
                      <td key={ft.id} className="py-3 px-2 text-right font-mono tabular-nums">
                        <div className="text-[14px]">{v != null ? Number(v).toFixed(2) : '—'}</div>
                        {cd && cd.diff != null && (
                          <div
                            className={`mt-1 text-[12.5px] font-bold inline-flex items-center gap-1 px-2 py-1 rounded ${cd.direction === 'above' ? 'bg-[#fee2e2] text-[#991b1b]' : cd.direction === 'below' ? 'bg-[#dcfce7] text-[#166534]' : 'text-[#8a8579]'}`}
                            title={`vs ${cd.vs_name || 'referencia'} Q${cd.competitor_avg}${cd.competitor_count > 1 ? ` (n=${cd.competitor_count})` : ''}`}
                            data-testid={`diff-${p.id}-${ft.id}`}
                          >
                            {cd.direction === 'above' ? <TrendingUp size={12}/> : cd.direction === 'below' ? <TrendingDown size={12}/> : null}
                            {cd.diff > 0 ? '+' : ''}{cd.diff.toFixed(2)}
                            {cd.pct != null && <span className="text-[10.5px] opacity-70">({cd.pct > 0 ? '+' : ''}{cd.pct.toFixed(1)}%)</span>}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className="py-3 px-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${p.gps_anomaly?'bg-[#fee2e2] text-[#991b1b]':p.status==='rejected'?'bg-[#e8e5de] text-[#5a5a5a]':'bg-[#1c1c1c] text-[#f8f6f1]'}`}>
                      {p.gps_anomaly && <AlertTriangle size={9}/>}
                      {p.gps_anomaly?'Anomalía':p.status==='rejected'?'Rechazada':'Tracking'}
                    </span>
                    {p.gps_anomaly && (
                      <p className="text-[10px] text-[#991b1b] mt-1 max-w-[190px] leading-snug" data-testid={`anomaly-reason-${p.id}`}>
                        {p.distance_m != null ? `GPS a ${p.distance_m} m de la estación (máx. ${p.gps_radius_m || 100} m)` : 'Enviada sin GPS (el teléfono no obtuvo ubicación)'}
                        {p.photo_gps_distance_m != null && p.photo_gps_distance_m > 100 ? ` · GPS de la foto a ${p.photo_gps_distance_m} m` : ''}
                      </p>
                    )}
                    {p.anomaly_reviewed && (
                      <p className="text-[10px] text-[#166534] mt-0.5" data-testid={`anomaly-reviewed-tag-${p.id}`}>✓ Revisada{p.anomaly_reviewed_by ? ` · ${p.anomaly_reviewed_by}` : ''}</p>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {p.gps_anomaly && !p.anomaly_reviewed && (
                        <button
                          onClick={() => markAnomalyReviewed(p)}
                          className="text-[11px] px-2.5 py-1 rounded-full border border-[#e8e5de] bg-white text-[#15803d] hover:bg-[#dcfce7] inline-flex items-center gap-1"
                          title="Archivar la anomalía sin aprobar ni rechazar"
                          data-testid={`btn-anomaly-reviewed-${p.id}`}
                        >
                          <CheckCheck size={11}/> Revisado
                        </button>
                      )}
                      <button onClick={() => openDetail(p)} className="text-[12px] text-[#1c1c1c] underline underline-offset-2 inline-flex items-center gap-1" data-testid={`btn-detail-${p.id}`}>
                        {p.photo_url && <Camera size={11}/>}
                        {p.status === 'pending' || p.status === 'flagged' ? 'Revisar' : 'Ver'}
                      </button>
                    </div>
                  </td>
                </tr>
                );
                    })}
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="py-12 text-center text-[#8a8579] text-[13px]">
                  Sin capturas en {showHistory ? 'los últimos 30 días' : 'los últimos 2 días'} para esta categoría
                  {!showHistory && hiddenByCutoff > 0 && (
                    <button onClick={() => setShowHistory(true)} className="block mx-auto mt-2 text-[12px] text-[#0f1d6f] underline" data-testid="empty-show-history-btn">
                      Ver {hiddenByCutoff} captura(s) más antigua(s) · histórico 30 días
                    </button>
                  )}
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!photoOpen} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent className="max-w-3xl bg-[#f8f6f1] max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{photoOpen?.station_name}</DialogTitle></DialogHeader>
          {photoOpen && (
            <div className="space-y-4">
              {photoOpen.photo_url && (
                <ZoomableImage src={photoURL(photoOpen.photo_url)} alt="Tablero" maxH="40vh" testId="approval-photo"/>
              )}
              <p className="text-[11px] text-[#8a8579]">
                Capturado por {photoOpen.captured_by_name} · disp {photoOpen.distance_m} m
                {photoOpen.photo_gps_distance_m != null && (
                  <span className={Math.abs(photoOpen.distance_m - photoOpen.photo_gps_distance_m) > 100 ? 'text-[#991b1b] font-medium' : ''}>
                    {' '}· EXIF foto {photoOpen.photo_gps_distance_m} m
                  </span>
                )}
                {' '}· {photoOpen.service_type === 'self' ? 'Autoservicio' : 'Servicio completo'} · {new Date(photoOpen.captured_at).toLocaleString('es-GT')}
              </p>
              {photoOpen.photo_gps_distance_m != null && Math.abs(photoOpen.distance_m - photoOpen.photo_gps_distance_m) > 100 && (
                <div className="bg-[#fee2e2] border border-[#fecaca] rounded-md p-2 text-[11.5px] text-[#991b1b] flex items-center gap-1.5">
                  <AlertTriangle size={12}/> Discrepancia GPS: el dispositivo y el EXIF de la foto difieren más de 100 m. Posible manipulación.
                </div>
              )}
              {photoOpen.notes && <p className="text-[12px] italic text-[#5a5a5a]">"{photoOpen.notes}"</p>}

              <div className="bg-white border border-[#e8e5de] rounded-lg p-4">
                <p className="text-[11.5px] uppercase tracking-wider text-[#8a8579] mb-3">Precios capturados, diferencial vs competidores y precio aprobado</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {FUEL_TYPES.map(ft => {
                    const cd = diffMap[photoOpen.id]?.competitor_diffs?.[ft.id];
                    const captured = photoOpen.prices?.[ft.id];
                    return (
                      <div key={ft.id} className="border border-[#e8e5de] rounded-md p-3 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <p className="text-[11px] uppercase tracking-wider text-[#3a3a3a]">{ft.name}</p>
                          {cd && cd.diff != null && (
                            <span className={`text-[12.5px] font-bold inline-flex items-center gap-1 px-2 py-0.5 rounded ${cd.direction === 'above' ? 'bg-[#fee2e2] text-[#991b1b]' : cd.direction === 'below' ? 'bg-[#dcfce7] text-[#166534]' : 'text-[#8a8579]'}`}>
                              {cd.direction === 'above' ? <TrendingUp size={11}/> : <TrendingDown size={11}/>}
                              {cd.diff > 0 ? '+' : ''}{cd.diff.toFixed(2)}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-[#5a5a5a]">
                          Capturado: <span className="font-mono font-medium text-[#1c1c1c]">{captured != null ? `Q${Number(captured).toFixed(2)}` : '—'}</span>
                        </p>
                        {cd && cd.competitor_avg != null && (
                          <p className="text-[10.5px] text-[#8a8579]">vs Q{cd.competitor_avg} avg comp. (n={cd.competitor_count})</p>
                        )}
                        <div>
                          <Label className="text-[10.5px] text-[#8a8579] uppercase tracking-wider">Precio aprobado</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={approvedPrices[ft.id] ?? ''}
                            onChange={e => setApprovedPrices({ ...approvedPrices, [ft.id]: e.target.value })}
                            disabled={photoOpen.status !== 'pending' && photoOpen.status !== 'flagged'}
                            className="h-9 mt-0.5 bg-[#fafaf6] font-mono"
                            data-testid={`input-approved-${photoOpen.id}-${ft.id}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {(photoOpen.status === 'pending' || photoOpen.status === 'flagged') && (
                <>
                  <div>
                    <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Notas de revisión (opcional)</Label>
                    <Input
                      value={reviewNotes}
                      onChange={e => setReviewNotes(e.target.value)}
                      placeholder="Comentario sobre el ajuste de precio…"
                      className="mt-1 bg-white"
                      data-testid="input-review-notes"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button onClick={() => submitApproval('reject')} disabled={submitting} variant="outline" className="press-soft border-[#7c1d2e]/30 text-[#7c1d2e]" data-testid="btn-reject-detail">
                      <X size={13} className="mr-1.5"/> Rechazar
                    </Button>
                    <Button onClick={() => submitApproval('approve')} disabled={submitting} className="bg-[#1c1c1c] hover:bg-black text-[#f8f6f1]" data-testid="btn-approve-detail">
                      <Check size={13} className="mr-1.5"/> Aprobar con estos precios
                    </Button>
                  </div>
                </>
              )}

              {photoOpen.review_notes && photoOpen.status === 'approved' && (
                <p className="text-[11.5px] text-[#5a5a5a] italic">Nota de revisión: "{photoOpen.review_notes}"</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPrices;
